// De redactiekaart, in de vorm die op 26 augustus is vastgesteld.
//
// DICHT (in de stroom):
//   pil met de categorie · tijd rechts · grote kop · één zin · bij synthese de
//   lagenmeter · bewijsregel. Geen "open"-knop: de hele kaart is het klikdoel en
//   een subtiele hover zegt dat (owner 26-08). Geen linkerrail: de PIL zegt al
//   wie er spreekt, en een gekleurde rand erbij werd te veel.
//
// OPEN (de item-modal), zeven blokken in deze volgorde:
//   1 kop + herkomst · 2 duiding mét de bron in de zin · 3 wat dit kan
//   betekenen · 4 advies · 5 één bronrij · 6 wat hieronder hangt · 7 vraag door
//
// DE BIJLAGE IS WEG. Tot v1.6 stonden de bronnen op drie plekken tegelijk
// ("waarop dit rust" met citaten, "voedt op dit platform", en de bronnenlijst),
// met hetzelfde document twee of drie keer omdat `bronnen[]` bewijsREGELS telt
// en geen documenten. Nu: ontdubbeld op document, de bron in de zin als pil, en
// één trechterregel eronder.
//
// EN JE KUNT IN DE KAART OP KAARTEN KLIKKEN (owner 26-08): een trend, een
// vaardigheid, een regeling of een bron opent als tweede laag BINNEN de modal,
// met "terug naar de kaart". De doorklik naar het tabblad blijft als knop
// bestaan — hij is dan een keuze en geen omweg.

import { useState } from "react";

import { Bewijsbalk, WIE_WOORD } from "./Bewijs";
import { Bronpil, Bronrij } from "./Bronpil";
import { VisueelBlok } from "./KaartVisueel";
import type { EditieKaartData, KaartBron, KaartLanding } from "../contracts";
import type { Bronbundel } from "../lib/kaartas";
import {
  bewijsRegel,
  bronbundels,
  bronTitel,
  lagenVan,
  pilVan,
  visueelVan,
} from "../lib/kaartas";
import { versheidToon } from "../lib/schermen";
import { color, font, radius, size, weight } from "../tokens";
import { Modal } from "./ui";
import { Verwijzingen, type VerwijzingKlik } from "./Verwijzing";

/** Waar de doorklik heen gaat — de workspace bedraadt dit. */
export type KaartOpenen = (landing: KaartLanding) => void;

const TREDE_UITLEG: Record<string, string> = {
  expliciete_koppeling:
    "De bron legt dit verband zelf, of de koppeling is expliciet vastgelegd in de configuratie.",
  gedeelde_as:
    "De twee kanten delen een onderwerp, een gebied of een categorie. De koppeling is echt, " +
    "maar hij komt van ons en niet uit één bron.",
  alleen_semantisch:
    "Deze twee lijken op elkaar volgens de tekstvergelijking. Geen enkele bron legt dit " +
    "verband; het is een interpretatie.",
};

// De scenariokaart (redactie-v1.8): hier is het vooruitkijk-blok niet een
// toegift onder een kaart maar de kaart zelf, en dat mag de lezer weten vóór
// hij klikt. De spelregels eronder staan in config/toekomst.yaml en worden in
// de schrijfronde afgedwongen — dit label is de zichtbare kant daarvan.
const VOORUITBLIK_UITLEG =
  "Deze kaart trekt door wat er gemeten is. Geen bron zegt dit letterlijk; " +
  "de kaart geeft twee of drie scenario's mét de tegenkant, en nooit een " +
  "kanspercentage.";

const LANDING_TEKST: Record<string, string> = {
  paneel: "Bekijk het detail",
  tab: "Open de lijst met dit filter",
  reeks: "Bekijk de hele reeks",
  // B1 (26-08): de vraag wordt INGEVULD, niet verstuurd — de knop belooft
  // dus ook niet meer dat hij hem stelt.
  chat: "Neem deze vraag mee naar de chat",
};

function Badge({
  tekst,
  toon = "grijs",
  titel,
}: {
  tekst: string;
  toon?: "grijs" | "amber" | "blauw" | "groen";
  titel?: string;
}) {
  const palet = {
    grijs: { bg: color.lineFaint, fg: color.soft, br: color.border },
    amber: { bg: color.accentAmberBg, fg: color.accentAmber, br: `${color.accentAmber}33` },
    blauw: { bg: "#eff6ff", fg: color.accentBlue, br: `${color.accentBlue}33` },
    groen: { bg: "#ecfdf5", fg: color.accentGreen, br: `${color.accentGreen}33` },
  }[toon];
  return (
    <span
      title={titel}
      style={{
        fontSize: size.label,
        color: palet.fg,
        background: palet.bg,
        border: `1px solid ${palet.br}`,
        borderRadius: radius.pill,
        padding: "3px 9px",
        whiteSpace: "nowrap",
      }}
    >
      {tekst}
    </span>
  );
}

/** Het label binnen de modal, in het V2-idioom (11/600 uppercase). */
function ML({ children, kleur }: { children: React.ReactNode; kleur?: string }) {
  return (
    <div
      style={{
        fontSize: size.label,
        fontWeight: weight.medium,
        color: kleur ?? color.faint,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

/* ── de dichte kaart ──────────────────────────────────────────────────────── */

/** De pil zegt welke categorie de kaart heeft, en daarmee wie er spreekt. */
function PilRij({ kaart }: { kaart: EditieKaartData }) {
  const pil = pilVan(kaart);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginBottom: 10,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10.5,
          fontWeight: weight.medium,
          color: pil.kleur,
          background: `${pil.kleur}12`,
          border: `1px solid ${pil.kleur}33`,
          borderRadius: radius.pill,
          padding: "2.5px 10px",
          whiteSpace: "nowrap",
        }}
        title={`${pil.label}: ${pil.spreker}`}
      >
        <span
          style={{ width: 5, height: 5, borderRadius: 99, background: pil.kleur, display: "inline-block" }}
        />
        {pil.label}
      </span>
      {/* F2 (bouwrun R1): "recent" is geen nieuws — grijs, niet groen. */}
      {kaart.versheidLabel && <Badge tekst={kaart.versheidLabel} toon={versheidToon(kaart.versheid)} />}
    </div>
  );
}

/** Bij synthese: de vijf lagen, mét de lege — dat zegt hoe stevig dit staat. */
function Lagenmeter({ kaart }: { kaart: EditieKaartData }) {
  const lagen = lagenVan(kaart);
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 6, marginTop: 14 }}
      title="De lagen onder deze kaart. Een lege laag is geen fout maar een meting: die kant is er niet."
    >
      {lagen.map((l) => (
        <div key={l.label}>
          <div
            style={{
              height: 5,
              borderRadius: 99,
              background: l.gemeten ? color.accentPurple : color.border,
            }}
          />
          <div style={{ fontSize: size.label, color: color.faint, marginTop: 5 }}>{l.label}</div>
          <div
            style={{
              fontSize: 10,
              color: l.gemeten ? color.soft : color.faint,
              lineHeight: 1.35,
              marginTop: 1,
            }}
          >
            {l.waarde}
          </div>
        </div>
      ))}
    </div>
  );
}

function Bewijsregel({ kaart }: { kaart: EditieKaartData }) {
  const b = bewijsRegel(kaart);
  return (
    <div
      style={{
        fontSize: size.label,
        color: color.faint,
        paddingTop: 10,
        marginTop: 12,
        borderTop: `1px solid ${color.line}`,
      }}
    >
      <b style={{ color: color.soft, fontWeight: weight.medium }}>{b.sterk}</b>
      {b.rest ? ` · ${b.rest}` : ""}
      {/* Methode v3: het kleine bewijsbalkje met wat er mist. */}
      {kaart.bewijs && (
        <>
          {" · "}
          <Bewijsbalk bewijs={kaart.bewijs} klein style={{ verticalAlign: "middle" }} />
        </>
      )}
    </div>
  );
}

/**
 * De DICHTE kaart. Klikken opent de item-modal; er staat geen "open"-knop op,
 * want de hele kaart is het klikdoel en de hover zegt dat al (owner 26-08).
 */
export function EditieKaart({
  kaart,
  onKlik,
  onVerwijzing,
}: {
  kaart: EditieKaartData;
  onKlik: (kaart: EditieKaartData) => void;
  onVerwijzing?: VerwijzingKlik;
}) {
  const pil = pilVan(kaart);
  const synthese = pil.id === "synthese";
  const interpretatie = kaart.combinatietrede === "alleen_semantisch";
  const vooruitblik = kaart.vorm === "scenariokaart";

  return (
    <div
      onClick={() => onKlik(kaart)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onKlik(kaart);
      }}
      style={{
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        background: color.surface,
        padding: "18px 20px",
        marginBottom: 8,
        fontFamily: font,
        cursor: "pointer",
        transition: "all 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color.disabled;
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,.06)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = color.line;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <PilRij kaart={kaart} />
      {/* Geen afkapping en geen lengtevlag: de kop mag lang zijn (owner 26-08).
          Wat telt is dat hij iets BEWEERT en niet de zin eronder herhaalt, en
          dat toetst de schrijfronde. */}
      <div
        style={{
          fontSize: 19,
          fontWeight: weight.bold,
          color: color.ink,
          lineHeight: 1.3,
          letterSpacing: "-0.012em",
        }}
      >
        {kaart.kop}
      </div>
      <div style={{ fontSize: size.body, color: color.muted, lineHeight: 1.6, marginTop: 8 }}>
        <Verwijzingen tekst={kaart.lead} onOpen={onVerwijzing} />
      </div>
      {/* De scenariokaart (redactie-v1.8) draagt hetzelfde amber signaal als de
          rest van het platform: wat hier staat is een doortrekking, geen bewijs.
          De scenario's zelf staan in het gestippelde kader in de open kaart. */}
      {(interpretatie || vooruitblik) && (
        <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {vooruitblik && (
            <Badge
              tekst="⤳ blik in de toekomst"
              toon="amber"
              titel={VOORUITBLIK_UITLEG}
            />
          )}
          {interpretatie && (
            <Badge tekst="interpretatie" toon="amber" titel={TREDE_UITLEG[kaart.combinatietrede]} />
          )}
        </div>
      )}
      {/* Niet op de vraag-kaart: een vraag claimt geen bewijslagen, en een
          meter die vijf keer "geen" zegt naast een vraag las als defect
          (poetsronde-audit 28-08). */}
      {synthese && kaart.vorm !== "vraag_aan_jou" && <Lagenmeter kaart={kaart} />}
      <Bewijsregel kaart={kaart} />
    </div>
  );
}

/* ── de kaarten IN de kaart ───────────────────────────────────────────────── */

type Subkaart = {
  soort: string;
  kleur: string;
  titel: string;
  /** Het getal dat er bij deze soort toe doet — rechts in de rij. */
  kerncijfer: string;
  /** De volle zin achter het kerncijfer (tooltip), waar die er is. */
  uitleg?: string;
  landing?: KaartLanding;
};

function subkaartenVan(kaart: EditieKaartData, bundels: Bronbundel[]): Subkaart[] {
  const g = (kaart.grond ?? {}) as Record<string, unknown>;
  const uit: Subkaart[] = [];

  const trend = g.trend as Record<string, unknown> | undefined;
  if (trend?.id) {
    uit.push({
      soort: "Trend",
      kleur: "#2563eb",
      titel: String(trend.titel ?? ""),
      kerncijfer: `${String(trend.bronnen ?? "?")} bronnen`,
      landing: { soort: "paneel", tab: "trends", id: String(trend.id) },
    });
  }

  for (const v of (g.vaardigheden as Record<string, unknown>[] | undefined) ?? []) {
    uit.push({
      soort: "Vaardigheid",
      kleur: "#059669",
      titel: String(v.naam ?? ""),
      kerncijfer: [
        v.prioriteit != null ? `prioriteit ${String(v.prioriteit).replace(".", ",")}` : null,
        v.quick_win ? "quickwin" : null,
      ]
        .filter(Boolean)
        .join(" · "),
      landing: v.id ? { soort: "paneel", tab: "skills", id: String(v.id) } : undefined,
    });
  }

  const skill = g.skill as Record<string, unknown> | undefined;
  if (skill?.id) {
    uit.push({
      soort: "Vaardigheid",
      kleur: "#059669",
      titel: String(skill.naam ?? ""),
      kerncijfer: `${String(skill.trends ?? "?")} trends`,
      landing: { soort: "paneel", tab: "skills", id: String(skill.id) },
    });
  }

  for (const s of (g.subsidie as Record<string, unknown>[] | undefined) ?? []) {
    uit.push({
      soort: "Regeling",
      kleur: "#d97706",
      titel: String(s.naam ?? ""),
      // Reviewronde W1: de editie is bewaard, dus status MÉT herkomst en dag
      // (status_kort); de volle zin als uitleg. Oude edities hebben alleen status.
      // Review fase 6: een automatisch gevonden regeling draagt haar
      // voorbehoud ook op de ketenkaart (nagekeken === false; oude edities
      // kennen het veld niet en blijven zoals ze waren).
      kerncijfer:
        String(s.status_kort ?? s.status ?? "status onbekend") +
        (s.nagekeken === false ? " · nieuw gevonden, nog niet nagekeken" : ""),
      uitleg: typeof s.status_tekst === "string" ? s.status_tekst : undefined,
      landing: { soort: "tab", tab: "funding" },
    });
  }

  for (const b of bundels) {
    uit.push({
      soort: "Bron",
      kleur: "#0891b2",
      titel: bronTitel(b.bron),
      // Methode v3: wie het zegt in plaats van het gezagscijfer.
      kerncijfer: [
        b.bron.uitgever,
        b.bron.wie !== undefined
          ? (b.bron.wie ? (WIE_WOORD[b.bron.wie] ?? b.bron.wie) : null)
          : b.bron.gezag != null
            ? `gezag ${b.bron.gezag}`
            : null,
      ]
        .filter(Boolean)
        .join(" · "),
      landing: b.bron.documentId
        ? { soort: "paneel", tab: "sources", id: b.bron.documentId }
        : undefined,
    });
  }

  return uit;
}

function SubkaartRij({
  item,
  laatste,
  onKlik,
}: {
  item: Subkaart;
  laatste: boolean;
  onKlik?: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onKlik}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={!onKlik}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        width: "100%",
        background: hover && onKlik ? color.surfaceAlt : "none",
        border: "none",
        borderBottom: laatste ? "none" : `1px solid ${color.lineFaint}`,
        padding: "9px 14px",
        font: "inherit",
        fontSize: 13,
        textAlign: "left",
        cursor: onKlik ? "pointer" : "default",
        transition: "background 0.12s",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 99,
          background: item.kleur,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontWeight: weight.medium,
          color: color.ink,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.titel}
      </span>
      <span
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 9,
          flexShrink: 0,
          fontSize: size.label,
          color: color.faint,
        }}
      >
        <span style={{ whiteSpace: "nowrap" }} title={item.uitleg}>
          {item.kerncijfer}
        </span>
        <span
          style={{ color: hover && onKlik ? color.ink : color.disabled, fontWeight: weight.medium }}
        >
          →
        </span>
      </span>
    </button>
  );
}

/* ── de open kaart ────────────────────────────────────────────────────────── */

/**
 * De item-modal in zeven blokken. `variant`-loos: dit ÍS de vorm, de oude stand
 * bestaat niet meer.
 */
export function KaartModal({
  kaart,
  rolLabel,
  onSluit,
  onOpen,
  onVerwijzing,
}: {
  kaart: EditieKaartData;
  rolLabel?: string | null;
  onSluit: () => void;
  onOpen?: KaartOpenen;
  onVerwijzing?: VerwijzingKlik;
}) {
  const pil = pilVan(kaart);
  const bundels = bronbundels(kaart);
  const subkaarten = subkaartenVan(kaart, bundels);
  const visueel = visueelVan(kaart);
  const interpretatie = kaart.combinatietrede === "alleen_semantisch";
  const flags = kaart.flags ?? [];
  const duidingWeggelaten = flags.includes("duiding_weggelaten");
  const adviesWeggelaten = flags.includes("advies_weggelaten");
  const implicatieWeggelaten = flags.includes("implicatie_weggelaten");
  const implicatie = ((kaart as { implicatie?: string }).implicatie ?? "").trim();
  const alineas = [kaart.lead, kaart.duiding].filter((t): t is string => Boolean(t && t.trim()));

  return (
    <Modal
      open
      title={kaart.kop}
      subtitle={[pil.label, pil.spreker, rolLabel ? `voor ${rolLabel}` : null]
        .filter(Boolean)
        .join(" · ")}
      onClose={onSluit}
    >
      <>
          {/* ── 1. herkomst ────────────────────────────────────────────── */}
          {kaart.subjectNaam && (
            <button
              onClick={() => {
                onSluit();
                onOpen?.(kaart.landing);
              }}
              style={{
                border: "none",
                background: "none",
                padding: 0,
                marginBottom: 16,
                fontSize: size.label,
                color: color.accentBlue,
                fontWeight: weight.medium,
                fontFamily: "inherit",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              via {kaart.subjectNaam} →
            </button>
          )}

          {/* ── 2. duiding, met de bron in de zin ──────────────────────── */}
          <div style={{ marginBottom: 18 }}>
            {alineas.map((tekst, i) => (
              <p
                key={i}
                style={{
                  fontSize: size.body,
                  color: i === 0 ? color.muted : color.body,
                  lineHeight: 1.75,
                  margin: i === alineas.length - 1 ? 0 : "0 0 10px",
                }}
              >
                <Verwijzingen tekst={tekst} onOpen={onVerwijzing} />
                {bundels.length > 0 && <Bronpil bundels={bundels} />}
              </p>
            ))}
            {!kaart.duiding && duidingWeggelaten && (
              <div style={{ fontSize: size.meta, color: color.faint, fontStyle: "italic", marginTop: 8 }}>
                De toelichting bij deze kaart is weggelaten: er stond een getal in dat niet uit de
                meting kwam.
              </div>
            )}
            {interpretatie && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: size.meta,
                  color: color.accentAmber,
                  lineHeight: 1.55,
                }}
              >
                {TREDE_UITLEG[kaart.combinatietrede]}
              </div>
            )}
          </div>

          {/* ── 3. blik in de toekomst ─────────────────────────────────── */}
          {/* B7 (feedbackronde 27-08): één naam en één idioom voor de
              vooruitkijk-blokken, hier én in de chat — de owner koos "Blik
              in de toekomst". Gestippeld amber = speculatie, nooit bewijs. */}
          {(implicatie || implicatieWeggelaten) && (
            <div
              style={{
                background: color.accentAmberBg,
                border: `1px dashed ${color.accentAmber}66`,
                borderRadius: radius.block,
                padding: "12px 16px",
                marginBottom: 18,
              }}
            >
              <ML kleur={color.accentAmber}>⤳ Blik in de toekomst · interpretatie</ML>
              <div style={{ fontSize: size.meta, color: "#8a5108", lineHeight: 1.6 }}>
                {implicatie ||
                  "Weggelaten: er stond een getal in dat de meting niet draagt. Liever geen " +
                    "vooruitblik dan een die nergens op rust."}
              </div>
            </div>
          )}

          {/* ── 4. advies ──────────────────────────────────────────────── */}
          <div
            style={{
              background: color.surfaceQuote,
              borderLeft: `3px solid ${kaart.advies ? color.ink : color.disabled}`,
              borderRadius: "0 8px 8px 0",
              padding: "12px 16px",
              marginBottom: 18,
            }}
          >
            <ML kleur={kaart.advies ? color.ink : color.faint}>Advies</ML>
            <div
              style={{
                fontSize: size.meta,
                color: kaart.advies ? color.body : color.soft,
                lineHeight: 1.6,
              }}
            >
              {kaart.advies ||
                (adviesWeggelaten
                  ? "Het advies bij deze kaart is weggelaten: er stond een getal in dat niet " +
                    "uit de meting kwam. Liever geen advies dan een handeling op een " +
                    "verzonnen bedrag of datum."
                  : "Deze kaart komt uit een generatie die dit veld nog niet schreef. Een " +
                    "advies moet langs dezelfde getallentoets en verificatiepas als de rest " +
                    "van de kaart; tot die generatie draait laten we het leeg in plaats van " +
                    "iets te verzinnen dat de meting niet draagt.")}
            </div>
          </div>

          {/* ── 5. het bewijs: de gemeten reeks en de bronrij ───────────── */}
          {visueel && (visueel.soort === "reeks" || visueel.soort === "scores") && (
            <div style={{ marginBottom: 18 }}>
              <ML>{visueel.soort === "reeks" ? "De gemeten reeks" : "De scores"}</ML>
              <VisueelBlok visueel={visueel} />
            </div>
          )}
          <div style={{ marginBottom: 18 }}>
            {bundels.length > 0 ? (
              <Bronrij bundels={bundels} />
            ) : (
              <div style={{ fontSize: size.label, color: color.faint, lineHeight: 1.6 }}>
                Geen document onder deze kaart: hij rust op een meting in de database. Wat die meet
                staat in de bewijsregel op de kaart zelf.
              </div>
            )}
          </div>

          {/* ── 6. wat hieronder hangt ─────────────────────────────────── */}
          {subkaarten.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <ML>Wat hieronder hangt</ML>
              <div
                style={{
                  border: `1px solid ${color.lineSoft}`,
                  borderRadius: radius.card,
                  overflow: "hidden",
                  background: color.surface,
                }}
              >
                {subkaarten.map((item, i) => (
                  <SubkaartRij
                    key={`${item.soort}-${i}`}
                    item={item}
                    laatste={i === subkaarten.length - 1}
                    onKlik={
                      item.landing && onOpen
                        ? () => {
                            onSluit();
                            onOpen(item.landing!);
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── 7. vraag door ──────────────────────────────────────────── */}
          {(kaart.vervolgvragen ?? []).length > 0 && (
            <div style={{ marginBottom: 4 }}>
              <ML>Vraag door</ML>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                {(kaart.vervolgvragen ?? []).map((v) => (
                  <button
                    key={v}
                    onClick={() => {
                      onSluit();
                      onOpen?.({ soort: "chat", vraag: v });
                    }}
                    style={{
                      fontSize: size.meta,
                      color: color.body,
                      background: color.surface,
                      border: `1px solid ${color.border}`,
                      borderRadius: radius.pill,
                      padding: "6px 14px",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      textAlign: "left",
                    }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── de twee uitgangen ──────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              gap: 10,
              paddingTop: 18,
              marginTop: 20,
              borderTop: `1px solid ${color.lineFaint}`,
            }}
          >
            <button
              onClick={() => {
                onSluit();
                onOpen?.(kaart.landing);
              }}
              style={{
                flex: 1,
                background: color.ink,
                border: "none",
                color: color.surface,
                padding: "10px",
                borderRadius: radius.input,
                fontSize: size.meta,
                fontWeight: weight.medium,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              {LANDING_TEKST[kaart.landing.soort] ?? "Bekijk"} →
            </button>
            <button
              onClick={() => {
                onSluit();
                onOpen?.({ soort: "chat", vraag: `Vertel meer over: ${kaart.kop}` });
              }}
              style={{
                flex: 1,
                background: color.surface,
                border: `1px solid ${color.border}`,
                color: color.body,
                padding: "10px",
                borderRadius: radius.input,
                fontSize: size.meta,
                fontWeight: weight.medium,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Bespreek in chat →
            </button>
          </div>
      </>
    </Modal>
  );
}

export type { KaartBron };
